import { createContext, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { req_mutations } from "@/config/endpoints";
import type { Requirement } from "@/features/stage/api/useRequirements";

/**
 * REQUIRED-FIELD MARKS — the red star a field earns from Stage & Status →
 * Requirements.
 *
 * A requirement row (`Fields` / `Custom` / `Modules`) is a GATE the server's
 * `RequirementValidator` enforces on a stage/status move. Nothing in the
 * product surfaced that on the field itself, so an admin who configured
 * "Email is required at Application · Submitted" saw a bare "Email" label in
 * the drawer and in Add Lead, and a counsellor found out only when the move
 * bounced. This module answers ONE question for any field on screen:
 * "does a gate ahead of this lead need it?" — and renders the star.
 *
 * WHICH GATES COUNT (mirrors the validator, erring towards showing the star):
 *   - enforceable rows only — `isInfo` rows are a documents checklist, never a
 *     gate, and never star anything;
 *   - a gate applies when it sits in the entity's CURRENT stage (any status)
 *     or any LATER stage. The validator enforces same-stage forward status
 *     moves against every gate up to the target status, and forward stage
 *     moves against every gate on the stages being entered — so "current
 *     stage or later" is exactly the set that can still block this lead.
 *     Gates on earlier stages are behind it and are never re-checked;
 *   - `specific` gates follow the same rule (they fire on landing on their
 *     status, which is ahead or in the current stage);
 *   - Add Lead has no position yet (creation is before the first status), so
 *     EVERY enforceable gate applies — the server does not run the validator
 *     on create, so there the star is a promise ("you will need this"), not
 *     a submit block.
 *
 * Deals inherit the parent lead's built-in fields and lead-level custom fields
 * (the checkers fall back to the lead row), so the same mark shows on the lead
 * fields whether a lead or one of its deals is the active entity.
 *
 * Star colour: red = a blocking gate needs it; amber = warn-only (the move
 * still goes through). The tooltip names the FIRST place it is needed.
 */
export type RequiredFieldMark = {
  /** At least one applicable gate blocks the move. */
  blocking: boolean;
  /** "Application · Submitted" — the earliest gate that needs the field. */
  where: string;
  /**
   * Verbatim tooltip, when the mark is NOT a stage gate — the tenant-wide
   * Add Lead policy ("Required ✓" in Customize Add Lead) uses it, since
   * "Required to reach <where>" has no stage to name.
   */
  reason?: string;
};

export type RequiredFieldMarkKind = "field" | "custom" | "module";

/** Mirrors the server's `isRequirementExemptForRole` — one meaning of "exempt". */
export function isRequirementExemptForRole(
  req: Pick<Requirement, "excludedRoleIds"> | null | undefined,
  roleId: number | null | undefined,
): boolean {
  if (roleId == null || !Number.isFinite(Number(roleId))) return false;
  const list = req?.excludedRoleIds;
  if (!Array.isArray(list) || list.length === 0) return false;
  return list.some((id) => Number(id) === Number(roleId));
}

export interface RequiredFieldMarkIndex {
  /** Built-in lead columns, keyed by `Requirements.referenceKey` (phone, email, …). */
  fields: Map<string, RequiredFieldMark>;
  /** Custom fields, keyed by `CustomInput.id` (`Requirements.referenceId`). */
  custom: Map<number, RequiredFieldMark>;
  /** Niche modules, keyed by `ModuleConnecter.id` (`Requirements.referenceId`). */
  modules: Map<number, RequiredFieldMark>;
}

export interface PipelinePosition {
  stageId: number;
  statusId: number;
}

export const EMPTY_REQUIRED_FIELD_MARK_INDEX: RequiredFieldMarkIndex = {
  fields: new Map(),
  custom: new Map(),
  modules: new Map(),
};

type PipelineStage = {
  id: number;
  name?: string;
  order: number;
  statuses?: Array<{ id: number; name?: string; order: number }>;
};

/**
 * Pure resolver — exported for the unit test. `position === null` means
 * "not created yet" (Add Lead): every enforceable gate applies.
 */
export function buildRequiredFieldMarkIndex(
  requirements: Requirement[] | undefined,
  pipeline: PipelineStage[] | undefined,
  position: PipelinePosition | null,
  /**
   * The VIEWER's role. A gate whose `excludedRoleIds` lists it does not apply
   * to this person (the server's validator skips it for their moves), so it
   * must not star the field for them either. Null = no exemption.
   */
  viewerRoleId: number | null = null,
): RequiredFieldMarkIndex {
  if (!requirements?.length || !pipeline?.length) return EMPTY_REQUIRED_FIELD_MARK_INDEX;

  const stageById = new Map<number, PipelineStage>();
  const statusById = new Map<number, { name?: string; order: number }>();
  for (const s of pipeline) {
    stageById.set(Number(s.id), s);
    for (const st of s.statuses ?? []) {
      statusById.set(Number(st.id), st);
    }
  }

  // Unknown stage (another branch's fork, or a stale row) → fail SAFE: no
  // stars rather than wrong stars.
  const currentStage =
    position != null ? stageById.get(Number(position.stageId)) : null;
  if (position != null && !currentStage) return EMPTY_REQUIRED_FIELD_MARK_INDEX;

  type Hit = RequiredFieldMark & { stageOrder: number; statusOrder: number };
  const fields = new Map<string, Hit>();
  const custom = new Map<number, Hit>();
  const modules = new Map<number, Hit>();

  const fold = <K,>(map: Map<K, Hit>, key: K, hit: Hit) => {
    const prev = map.get(key);
    if (!prev) {
      map.set(key, hit);
      return;
    }
    // Earliest gate names the place; ANY blocking gate makes it blocking.
    const earlier =
      hit.stageOrder < prev.stageOrder ||
      (hit.stageOrder === prev.stageOrder && hit.statusOrder < prev.statusOrder);
    map.set(key, {
      blocking: prev.blocking || hit.blocking,
      where: earlier ? hit.where : prev.where,
      stageOrder: earlier ? hit.stageOrder : prev.stageOrder,
      statusOrder: earlier ? hit.statusOrder : prev.statusOrder,
    });
  };

  for (const r of requirements) {
    if (!r || r.isInfo) continue;
    if (isRequirementExemptForRole(r, viewerRoleId)) continue;
    if (r.type !== "Fields" && r.type !== "Custom" && r.type !== "Modules") {
      continue;
    }
    const gateStage = stageById.get(Number(r.stageId));
    if (!gateStage) continue;
    if (currentStage && gateStage.order < currentStage.order) continue;

    const gateStatus = statusById.get(Number(r.statusId));
    const hit: Hit = {
      blocking: r.isBlocking !== false,
      where: gateStatus?.name
        ? `${gateStage.name ?? "Stage"} · ${gateStatus.name}`
        : (gateStage.name ?? "a later stage"),
      stageOrder: gateStage.order,
      statusOrder: gateStatus?.order ?? Number.MAX_SAFE_INTEGER,
    };

    if (r.type === "Fields") {
      const key = String(r.referenceKey ?? "").trim();
      if (key) fold(fields, key, hit);
    } else if (r.type === "Custom") {
      const id = Number(r.referenceId);
      if (Number.isFinite(id) && id > 0) fold(custom, id, hit);
    } else {
      const id = Number(r.referenceId);
      if (Number.isFinite(id) && id > 0) fold(modules, id, hit);
    }
  }

  const strip = <K,>(map: Map<K, Hit>): Map<K, RequiredFieldMark> =>
    new Map(
      [...map.entries()].map(([k, v]) => [
        k,
        { blocking: v.blocking, where: v.where },
      ]),
    );

  return { fields: strip(fields), custom: strip(custom), modules: strip(modules) };
}

/**
 * The tenant-wide Add Lead policy (Customize Add Lead → "Required ✓"), keyed
 * the way `LeadFormConfig.requiredFields` stores it: "basic:email",
 * "source:leadSource", "pref:<customInputId>". Folded into the SAME index the
 * stage gates fill, so every label that already asks `useRequiredFieldMark`
 * shows the star for both reasons with no per-field wiring.
 *
 * A stage mark on the same field wins (its tooltip names the gate); the
 * policy only fills the gaps. Both are red — the submit guard
 * (`missingRequiredFields`) blocks on the policy exactly like a gate does.
 */
const ORG_REQUIRED_FIELD_KEY: Record<string, string> = {
  "basic:name": "name",
  "basic:mobile": "phone",
  "basic:email": "email",
  "basic:gender": "gender",
  "source:branch": "branch",
  "source:leadSource": "leadSource",
  "source:campaign": "campaign",
  "source:user": "user",
};

export function mergeOrgRequiredIntoIndex(
  index: RequiredFieldMarkIndex,
  orgRequiredKeys: Iterable<string> | null | undefined,
): RequiredFieldMarkIndex {
  const keys = [...(orgRequiredKeys ?? [])];
  if (keys.length === 0) return index;
  const mark: RequiredFieldMark = {
    blocking: true,
    where: "",
    reason: "Required by your organisation's Add Lead policy",
  };
  const fields = new Map(index.fields);
  const custom = new Map(index.custom);
  for (const key of keys) {
    if (key.startsWith("pref:")) {
      const id = Number(key.slice(5));
      if (Number.isFinite(id) && !custom.has(id)) custom.set(id, mark);
      continue;
    }
    const field = ORG_REQUIRED_FIELD_KEY[key];
    if (field && !fields.has(field)) fields.set(field, mark);
  }
  return { fields, custom, modules: index.modules };
}

/** Tenant-wide requirement rows — same key/fetch `useDocumentRequirementNames` uses. */
export function useTenantRequirements() {
  return useQuery<Requirement[]>({
    queryKey: ["requirements", "all"],
    queryFn: async () => {
      const res = await axiosInstance.get(req_mutations);
      return res.data?.requirements ?? [];
    },
  });
}

export const RequiredFieldMarksContext =
  createContext<RequiredFieldMarkIndex | null>(null);

/**
 * The mark for one field, or null. Null outside a provider, so every renderer
 * that calls this stays byte-identical on surfaces that don't opt in.
 */
export function useRequiredFieldMark(
  kind: RequiredFieldMarkKind,
  key: string | number | null | undefined,
): RequiredFieldMark | null {
  const index = useContext(RequiredFieldMarksContext);
  if (!index || key == null || key === "") return null;
  if (kind === "field") return index.fields.get(String(key)) ?? null;
  const id = Number(key);
  if (!Number.isFinite(id)) return null;
  return (kind === "custom" ? index.custom : index.modules).get(id) ?? null;
}

